import {
  canBundleDeliveryShareOrders,
  getDeliveryRouteLocationLabel as getOrderLocationLabel,
  getDeliveryRouteSelectionId,
  getSafeDayjs,
} from './delivery-share.utils'
import {
  getDeliveryRouteMapAddress,
  getDeliveryRouteMapLocationPoint,
  getDeliveryRouteMapPosition,
} from './delivery-route-map-model'

export function isOrderBlockedByRouteLocation(order, anchorOrder, selectedOrderIdSet) {
  return (
    !selectedOrderIdSet.has(getDeliveryRouteSelectionId(order)) &&
    anchorOrder &&
    !canBundleDeliveryShareOrders(anchorOrder, order)
  )
}

export function getDeliveryRouteMapOrderRoutePoint(order, geocodedPositions = {}) {
  return (
    getDeliveryRouteMapPosition(order, geocodedPositions) ??
    (getDeliveryRouteMapAddress(order) || null)
  )
}

export function getDeliveryRouteMapRoutePointKey(point) {
  if (typeof point === 'string') {
    return `address:${point}`
  }

  return `position:${Number(point?.lat).toFixed(6)}:${Number(point?.lng).toFixed(6)}`
}

export function getDeliveryRouteMapRoutePointGroups(orders = [], geocodedPositions = {}) {
  const groups = new Map()

  orders.forEach((order) => {
    const routePoint = getDeliveryRouteMapOrderRoutePoint(order, geocodedPositions)

    if (!routePoint) {
      return
    }

    const key = getDeliveryRouteMapRoutePointKey(routePoint)
    const group = groups.get(key) ?? {
      key,
      routePoint,
      orders: [],
    }

    group.orders.push(order)
    groups.set(key, group)
  })

  return Array.from(groups.values())
}

export function getDeliveryRouteMapOrderedRouteCandidate({
  originLocation,
  routeStartPosition,
  selectedOrders = [],
  geocodedPositions = {},
  travelMode = 'DRIVING',
  routingPreference,
} = {}) {
  if ((!originLocation && !routeStartPosition) || selectedOrders.length === 0) {
    return null
  }

  const origin = routeStartPosition ?? getDeliveryRouteMapLocationPoint(originLocation)
  const routePointGroups = []

  selectedOrders.forEach((order) => {
    const routePoint = getDeliveryRouteMapOrderRoutePoint(order, geocodedPositions)

    if (!routePoint) {
      return
    }

    const key = getDeliveryRouteMapRoutePointKey(routePoint)
    const previousGroup = routePointGroups.at(-1)

    if (previousGroup?.key === key) {
      previousGroup.orders.push(order)
      return
    }

    routePointGroups.push({
      key,
      routePoint,
      orders: [order],
    })
  })

  const routePointOrderCount = routePointGroups.reduce(
    (count, group) => count + group.orders.length,
    0
  )

  if (!origin || routePointOrderCount !== selectedOrders.length) {
    return null
  }

  const destinationGroup = routePointGroups.at(-1)
  const intermediateGroups = routePointGroups.slice(0, -1)

  return {
    destinationGroup,
    intermediateGroups,
    orderedOrderIds: selectedOrders.map(getDeliveryRouteSelectionId),
    request: {
      origin,
      destination: destinationGroup.routePoint,
      fields: ['path', 'durationMillis', 'distanceMeters'],
      ...(intermediateGroups.length > 0
        ? {
            intermediates: intermediateGroups.map((group) => ({
              location: group.routePoint,
            })),
          }
        : {}),
      travelMode,
      ...(routingPreference ? { routingPreference } : {}),
    },
  }
}

export function getDeliveryRouteMapOptimizedRouteCandidates({
  originLocation,
  routeStartPosition,
  selectedOrders = [],
  geocodedPositions = {},
  travelMode = 'DRIVING',
  routingPreference,
} = {}) {
  if ((!originLocation && !routeStartPosition) || selectedOrders.length === 0) {
    return []
  }

  const origin = routeStartPosition ?? getDeliveryRouteMapLocationPoint(originLocation)
  const routePointGroups = getDeliveryRouteMapRoutePointGroups(selectedOrders, geocodedPositions)
  const routePointOrderCount = routePointGroups.reduce(
    (count, group) => count + group.orders.length,
    0
  )

  if (!origin || routePointOrderCount !== selectedOrders.length) {
    return []
  }

  if (routePointGroups.length === 1) {
    const destinationGroup = routePointGroups[0]

    return [
      {
        destinationGroup,
        intermediateGroups: [],
        request: {
          origin,
          destination: destinationGroup.routePoint,
          fields: ['path', 'durationMillis', 'distanceMeters'],
          travelMode,
          ...(routingPreference ? { routingPreference } : {}),
        },
      },
    ]
  }

  return routePointGroups.map((destinationGroup) => {
    const intermediateGroups = routePointGroups.filter(
      (group) => group.key !== destinationGroup.key
    )

    return {
      destinationGroup,
      intermediateGroups,
      request: {
        origin,
        destination: destinationGroup.routePoint,
        fields: [
          'path',
          'durationMillis',
          'distanceMeters',
          'optimizedIntermediateWaypointIndices',
        ],
        intermediates: intermediateGroups.map((group) => ({ location: group.routePoint })),
        optimizeWaypointOrder: true,
        travelMode,
        ...(routingPreference ? { routingPreference } : {}),
      },
    }
  })
}

export function isDeliveryRouteMapNavigationOrder(order) {
  return !order?.is_delivered && !order?.is_route_missed
}

export function getDeliveryRouteMapOptimizedOrderIds(candidate, route) {
  if (!candidate) {
    return []
  }

  const intermediateGroups = candidate.intermediateGroups ?? []
  const intermediateCount = intermediateGroups.length
  const waypointOrder = route?.optimizedIntermediateWaypointIndices
  const orderedIntermediateIndexes =
    intermediateCount > 1 && Array.isArray(waypointOrder) ? waypointOrder.map(Number) : null
  const hasValidOptimizedOrder =
    orderedIntermediateIndexes?.length === intermediateCount &&
    [...orderedIntermediateIndexes]
      .sort((left, right) => left - right)
      .every((index, position) => index === position)

  if (intermediateCount > 1 && !hasValidOptimizedOrder) {
    return []
  }

  const orderedIntermediateGroups =
    intermediateCount > 1
      ? orderedIntermediateIndexes.map((index) => intermediateGroups[index])
      : intermediateGroups

  return [...orderedIntermediateGroups, candidate.destinationGroup]
    .filter(Boolean)
    .flatMap((group) => group.orders.map(getDeliveryRouteSelectionId))
}

export function getDeliveryRouteMapRouteDurationMillis(route) {
  const durationMillis = Number(route?.durationMillis)

  return Number.isFinite(durationMillis) ? durationMillis : Number.MAX_SAFE_INTEGER
}

export function getDeliveryRouteMapRouteDistanceMeters(route) {
  const distanceMeters = Number(route?.distanceMeters)

  return Number.isFinite(distanceMeters) && distanceMeters >= 0 ? distanceMeters : null
}

export function getDeliveryRouteMapSelectedRouteSignature(
  selectedOrders = [],
  geocodedPositions = {}
) {
  return selectedOrders
    .map((order) => {
      const position = getDeliveryRouteMapPosition(order, geocodedPositions)

      return [
        getDeliveryRouteSelectionId(order),
        order.location?.id ?? '',
        order.is_delivered ? 'delivered' : '',
        order.is_route_missed ? 'missed' : '',
        getDeliveryRouteMapAddress(order),
        position ? Number(position.lat).toFixed(6) : '',
        position ? Number(position.lng).toFixed(6) : '',
      ].join('/')
    })
    .join('|')
}

export function formatDeliveryRouteMapDuration(durationMillis) {
  const numericDurationMillis = Number(durationMillis)

  if (!Number.isFinite(numericDurationMillis) || numericDurationMillis <= 0) {
    return null
  }

  const totalMinutes = Math.max(Math.round(numericDurationMillis / 60000), 1)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`
  }

  if (hours > 0) {
    return `${hours}h`
  }

  return `${minutes}m`
}

export function formatDeliveryRouteMapDistance(distanceMeters) {
  const numericDistanceMeters = Number(distanceMeters)

  if (!Number.isFinite(numericDistanceMeters) || numericDistanceMeters <= 0) {
    return null
  }

  const miles = numericDistanceMeters / 1609.344
  const roundedMiles = miles < 10 ? Math.max(Math.round(miles * 10) / 10, 0.1) : Math.round(miles)

  return `${roundedMiles}mi`
}

export function formatDeliveryRouteMapRouteSummary(routeSummary) {
  const duration = formatDeliveryRouteMapDuration(routeSummary?.durationMillis)
  const distance = formatDeliveryRouteMapDistance(routeSummary?.distanceMeters)

  return duration && distance ? `Time/Distance: ${duration}/${distance}` : null
}

export function positionKey(position) {
  return `${Number(position?.lat).toFixed(6)}:${Number(position?.lng).toFixed(6)}`
}

export function getMapLatLngPosition(latLng) {
  const lat = typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat
  const lng = typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng
  const numericLat = Number(lat)
  const numericLng = Number(lng)

  return Number.isFinite(numericLat) && Number.isFinite(numericLng)
    ? { lat: numericLat, lng: numericLng }
    : null
}

export function getDeliveryRouteMapSelectionBounds(startLatLng, endLatLng) {
  const startPosition = getMapLatLngPosition(startLatLng)
  const endPosition = getMapLatLngPosition(endLatLng)

  if (!startPosition || !endPosition) {
    return null
  }

  return {
    east: Math.max(startPosition.lng, endPosition.lng),
    north: Math.max(startPosition.lat, endPosition.lat),
    south: Math.min(startPosition.lat, endPosition.lat),
    west: Math.min(startPosition.lng, endPosition.lng),
  }
}

export function getDeliveryRouteMapBoundsEdges(bounds) {
  if (!bounds) {
    return null
  }

  if (typeof bounds.getNorthEast === 'function' && typeof bounds.getSouthWest === 'function') {
    const northEast = getMapLatLngPosition(bounds.getNorthEast())
    const southWest = getMapLatLngPosition(bounds.getSouthWest())

    if (!northEast || !southWest) {
      return null
    }

    return {
      east: northEast.lng,
      north: northEast.lat,
      south: southWest.lat,
      west: southWest.lng,
    }
  }

  const east = Number(bounds.east)
  const north = Number(bounds.north)
  const south = Number(bounds.south)
  const west = Number(bounds.west)

  return [east, north, south, west].every(Number.isFinite) ? { east, north, south, west } : null
}

export function isDeliveryRouteMapPositionInsideBounds(position, bounds) {
  if (!position || !bounds) {
    return false
  }

  if (typeof bounds.contains === 'function') {
    return bounds.contains(position)
  }

  const edges = getDeliveryRouteMapBoundsEdges(bounds)

  if (!edges) {
    return false
  }

  const lat = Number(position.lat)
  const lng = Number(position.lng)
  const isInsideLatitude = lat >= edges.south && lat <= edges.north
  const isInsideLongitude =
    edges.west <= edges.east
      ? lng >= edges.west && lng <= edges.east
      : lng >= edges.west || lng <= edges.east

  return isInsideLatitude && isInsideLongitude
}

export function getDeliveryRouteMapSelectionOrderIds({
  anchorOrder,
  bounds,
  groups = [],
  selectedOrderIds = [],
}) {
  if (!bounds || groups.length === 0) {
    return []
  }

  const selectedOrderIdSet = new Set(selectedOrderIds)
  const nextOrderIds = []
  let routeAnchorOrder = anchorOrder ?? null

  groups
    .filter((group) => isDeliveryRouteMapPositionInsideBounds(group.position, bounds))
    .flatMap((group) => group.orders)
    .forEach((order) => {
      const orderId = getDeliveryRouteSelectionId(order)

      if (selectedOrderIdSet.has(orderId)) {
        return
      }

      if (!routeAnchorOrder) {
        routeAnchorOrder = order
        selectedOrderIdSet.add(orderId)
        nextOrderIds.push(orderId)
        return
      }

      if (isOrderBlockedByRouteLocation(order, routeAnchorOrder, selectedOrderIdSet)) {
        return
      }

      selectedOrderIdSet.add(orderId)
      nextOrderIds.push(orderId)
    })

  return nextOrderIds
}

export function getDeliveryRouteMapUniquePositionCount(orders = [], geocodedPositions = {}) {
  return new Set(
    orders
      .map((order) => getDeliveryRouteMapPosition(order, geocodedPositions))
      .filter(Boolean)
      .map(positionKey)
  ).size
}

export function getDeliveryRouteMapPositionGroups(orders = [], geocodedPositions = {}) {
  const groups = new Map()

  orders.forEach((order) => {
    const position = getDeliveryRouteMapPosition(order, geocodedPositions)

    if (!position) {
      return
    }

    const key = positionKey(position)
    const group = groups.get(key) ?? {
      key,
      position,
      orders: [],
    }

    group.orders.push(order)
    groups.set(key, group)
  })

  return Array.from(groups.values())
}

export function getDeliveryRouteMapListGroupKey(order) {
  const position = getDeliveryRouteMapPosition(order)

  if (position) {
    return `position:${positionKey(position)}`
  }

  const address = getDeliveryRouteMapAddress(order).toLowerCase()

  return address ? `address:${address}` : `order:${getDeliveryRouteSelectionId(order) ?? ''}`
}

export function compareDeliveryRouteMapListOrders(left, right) {
  const leftDueTimestamp = getSafeDayjs(left?.fulfillment_due_date)?.valueOf()
  const rightDueTimestamp = getSafeDayjs(right?.fulfillment_due_date)?.valueOf()

  return (
    (leftDueTimestamp ?? Number.MAX_SAFE_INTEGER) -
      (rightDueTimestamp ?? Number.MAX_SAFE_INTEGER) ||
    getOrderLocationLabel(left?.location).localeCompare(getOrderLocationLabel(right?.location)) ||
    getDeliveryRouteMapListGroupKey(left).localeCompare(getDeliveryRouteMapListGroupKey(right)) ||
    String(getDeliveryRouteSelectionId(left) ?? '').localeCompare(
      String(getDeliveryRouteSelectionId(right) ?? ''),
      'en',
      { numeric: true }
    )
  )
}

export function getDeliveryRouteMapHighlightedScrollOrderId(highlightedOrderIds = [], orders = []) {
  if (highlightedOrderIds.length === 0) {
    return null
  }

  const highlightedOrderIdSet = new Set(highlightedOrderIds)
  const visibleHighlightedOrders = orders.filter((order) =>
    highlightedOrderIdSet.has(getDeliveryRouteSelectionId(order))
  )

  if (visibleHighlightedOrders.length === 0) {
    return highlightedOrderIds[0] ?? null
  }

  return getDeliveryRouteSelectionId(
    visibleHighlightedOrders[Math.floor(visibleHighlightedOrders.length / 2)]
  )
}
